import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
    ArrowLeft,
    Heart,
    Share2,
    MapPin,
    Bed,
    Bath,
    Square,
    Star,
    Phone,
    Mail,
    MessageCircle,
    Calendar,
    Shield,
    Wifi,
    Car,
    Zap,
    Droplet,
    Waves,
    Dumbbell,
    ArrowUpDown,
    Trees,
    Shirt,
    Wind,
    Building,
    ChevronLeft,
    ChevronRight,
    X,
    Send,
    CheckCircle,
    AlertCircle,
    Clock,
    Home,
    Loader,
    PlayCircle,
    Box,
    Link2
} from 'lucide-react';
import { rawRequest } from '../../services/apiClient';
import { AMENITIES, getPropertyTypeName } from '../../constants/propertyConstants';
import SEO from '../../Components/Common/SEO';
import { useProperty, useAuth, useNotification } from '../../App';
import dashboardService from '../../services/dashboardService';

const AMENITY_ICONS = {
    Wifi, Car, Waves, Dumbbell, Shield, ArrowUpDown, Home, Trees, Shirt, Wind, Zap, Droplet
};

const FALLBACK_IMAGE = "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80";

const COMPANY_CONTACT = {
    name: 'Rent Utopia',
    subtitle: 'Property Management Team',
    phone: '+254 712 292 899',
    email: 'itorama254@gmail.com',
    whatsapp: '254712292899'
};

const PropertyDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { savedProperties, toggleSaveProperty } = useProperty();
    const { isAuthenticated } = useAuth();
    const { addNotification } = useNotification();

    const [property, setProperty] = useState(null);
    const [landlord, setLandlord] = useState(null);
    const [images, setImages] = useState([]);
    const [media, setMedia] = useState({ videoTourUrl: null, virtualTourUrl: null, otherMediaLinks: [] });
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [showImageModal, setShowImageModal] = useState(false);
    const [showContactForm, setShowContactForm] = useState(false);
    const [showScheduleForm, setShowScheduleForm] = useState(false);
    const [showApplicationForm, setShowApplicationForm] = useState(false);

    // Form states
    const [contactMessage, setContactMessage] = useState('');
    const [contactSubmitting, setContactSubmitting] = useState(false);
    const [contactError, setContactError] = useState(null);

    const [scheduleForm, setScheduleForm] = useState({
        date: '',
        time: '',
        message: ''
    });
    const [scheduleSubmitting, setScheduleSubmitting] = useState(false);
    const [scheduleError, setScheduleError] = useState(null);

    useEffect(() => {
        let cancelled = false;

        const loadProperty = async () => {
            setLoading(true);
            setNotFound(false);
            try {
                const data = await rawRequest(`/properties/${id}`);
                if (cancelled) return;
                setProperty(data.property);
                setLandlord(data.landlord || null);
            } catch (err) {
                // The backend currently surfaces a missing/inactive property as
                // a generic 500 rather than a 404 - treat any failure here as
                // "not found" since there's no other reliable signal.
                if (!cancelled) setNotFound(true);
            } finally {
                if (!cancelled) setLoading(false);
            }

            try {
                const imgData = await rawRequest(`/properties/${id}/images`);
                if (!cancelled) {
                    setImages((imgData.images || []).map((img) => img.url));
                    setMedia({
                        videoTourUrl: imgData.videoTourUrl || null,
                        virtualTourUrl: imgData.virtualTourUrl || null,
                        otherMediaLinks: imgData.otherMediaLinks || []
                    });
                }
            } catch (err) {
                // Images endpoint can fail independently of the core property
                // fetch - degrade to the fallback image rather than breaking the page.
                if (!cancelled) setImages([]);
            }
        };

        loadProperty();

        return () => { cancelled = true; };
    }, [id]);

    const isSaved = savedProperties.includes(parseInt(id));

    const formatPrice = (price) => {
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES',
            minimumFractionDigits: 0
        }).format(price);
    };

    const displayImages = images.length > 0 ? images : [FALLBACK_IMAGE];

    const locationLabel = property?.city || 'Kenya';

    const propertyTypeName = property ? getPropertyTypeName(property.propertyTypeId) : '';

    const title = property
        ? (property.propertyName || `${property.bedrooms} Bedroom ${propertyTypeName} in ${locationLabel}`)
        : '';

    const amenityDetails = (property?.amenityIds || [])
        .map((amenityId) => AMENITIES.find((a) => a.id === amenityId))
        .filter(Boolean);

    const toWhatsAppNumber = (phone) => {
        const digits = (phone || '').replace(/\D/g, '');
        return digits.startsWith('0') ? `254${digits.slice(1)}` : digits;
    };

    // When the backend has no landlord data at all for a listing, fall back
    // to the generic company contact. Once it does, only show a phone/email/
    // WhatsApp option if the landlord actually allowed that contact method -
    // if they didn't, that field comes back unset, and we don't substitute
    // the company's own number/address in its place.
    const landlordContact = landlord?.name
        ? {
            name: landlord.name,
            subtitle: 'Listed by',
            phone: property?.allowContactViaPhone !== false ? landlord.phone : null,
            email: property?.allowContactViaEmail !== false ? landlord.email : null,
            whatsapp: property?.allowContactViaPhone !== false && landlord.phone ? toWhatsAppNumber(landlord.phone) : null
        }
        : { ...COMPANY_CONTACT };

    const allowViewingRequests = property?.allowViewingRequests !== false;

    const handleSaveProperty = () => {
        toggleSaveProperty(parseInt(id));
    };

    const handleShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title,
                    text: `Check out this property: ${title}`,
                    url: window.location.href
                });
            } catch (error) {
                console.log('Error sharing:', error);
            }
        } else {
            navigator.clipboard.writeText(window.location.href);
            alert('Link copied to clipboard!');
        }
    };

    const handleOpenContactForm = () => {
        if (!isAuthenticated) {
            navigate('/login', { state: { from: location } });
            return;
        }
        setShowContactForm(true);
    };

    const handleContactSubmit = async (e) => {
        e.preventDefault();
        if (!property?.landlordId) return;

        setContactSubmitting(true);
        setContactError(null);
        try {
            await dashboardService.sendMessage({
                recipientId: property.landlordId,
                message: `Re: ${title} — ${contactMessage.trim()}`
            });
            setShowContactForm(false);
            setContactMessage('');
            addNotification('Message sent to the landlord!', 'success');
        } catch (err) {
            setContactError(err.message || 'Failed to send message. Please try again.');
        } finally {
            setContactSubmitting(false);
        }
    };

    const handleScheduleSubmit = async (e) => {
        e.preventDefault();
        setScheduleSubmitting(true);
        setScheduleError(null);
        try {
            const preferredDateTime = `${scheduleForm.date} ${scheduleForm.time}:00`;
            await rawRequest(`/properties/${id}/schedule-viewing`, {
                method: 'POST',
                body: {
                    preferredDateTime,
                    numberOfViewers: 1,
                    virtualViewingOk: false
                }
            });
            setShowScheduleForm(false);
            setScheduleForm({ date: '', time: '', message: '' });
            alert('Viewing scheduled successfully! You will receive a confirmation shortly.');
        } catch (err) {
            setScheduleError(
                err.status === 401
                    ? 'Please log in to schedule a viewing.'
                    : (err.message || 'Failed to schedule viewing. Please try again.')
            );
        } finally {
            setScheduleSubmitting(false);
        }
    };

    const nextImage = () => {
        setCurrentImageIndex((prev) => (prev === displayImages.length - 1 ? 0 : prev + 1));
    };

    const prevImage = () => {
        setCurrentImageIndex((prev) => (prev === 0 ? displayImages.length - 1 : prev - 1));
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-karibu-100 pt-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="animate-pulse">
                        <div className="h-8 bg-soil-100 rounded w-1/2 mb-6"></div>
                        <div className="h-96 bg-soil-100 rounded-xl mb-8"></div>
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                            <div className="lg:col-span-2 space-y-6">
                                <div className="h-6 bg-soil-100 rounded w-3/4"></div>
                                <div className="h-4 bg-soil-100 rounded w-full"></div>
                                <div className="h-4 bg-soil-100 rounded w-5/6"></div>
                            </div>
                            <div className="h-96 bg-soil-100 rounded-xl"></div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (notFound || !property) {
        return (
            <div className="min-h-screen bg-karibu-100 pt-20 flex items-center justify-center">
                <div className="text-center">
                    <AlertCircle className="w-16 h-16 text-murram-500 mx-auto mb-4" />
                    <h2 className="font-display text-2xl font-bold text-soil-900 mb-2">Property Not Found</h2>
                    <p className="text-soil-600 mb-6">The property you're looking for doesn't exist or has been removed.</p>
                    <button
                        onClick={() => navigate('/listings')}
                        className="bg-highland-500 hover:bg-highland-600 text-white px-6 py-3 rounded-lg font-medium"
                    >
                        Back to Listings
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-karibu-100 pt-20">
            <SEO
                title={title}
                description={`${title} for ${formatPrice(property.pricePerMonth)}/month. ${property.bedrooms} bedroom, ${property.bathrooms} bathroom ${propertyTypeName.toLowerCase()} available on Rent Utopia.`}
                canonicalPath={`/property/${id}`}
                image={displayImages[0]}
                jsonLd={{
                    "@context": "https://schema.org",
                    "@type": "Apartment",
                    "name": title,
                    "numberOfRooms": property.bedrooms,
                    "address": {
                        "@type": "PostalAddress",
                        "addressLocality": property.city || "Nairobi",
                        "addressCountry": "KE"
                    }
                }}
            />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <button
                        onClick={() => navigate(-1)}
                        className="flex items-center space-x-2 text-soil-600 hover:text-soil-900"
                    >
                        <ArrowLeft className="w-5 h-5" />
                        <span>Back to listings</span>
                    </button>

                    <div className="flex items-center space-x-3">
                        <button
                            onClick={handleShare}
                            className="flex items-center space-x-2 px-4 py-2 border border-soil-100 hover:border-highland-500 text-soil-600 hover:text-highland-500 rounded-lg transition-colors duration-200"
                        >
                            <Share2 className="w-4 h-4" />
                            <span>Share</span>
                        </button>

                        <button
                            onClick={handleSaveProperty}
                            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors duration-200 ${
                                isSaved
                                    ? 'bg-murram-500 hover:bg-murram-600 text-white'
                                    : 'border border-soil-100 hover:border-murram-500 text-soil-600 hover:text-murram-600'
                            }`}
                        >
                            <Heart className={`w-4 h-4 ${isSaved ? 'fill-current' : ''}`} />
                            <span>{isSaved ? 'Saved' : 'Save'}</span>
                        </button>
                    </div>
                </div>

                {/* Image Gallery */}
                <div className="relative mb-8">
                    <div className="relative h-96 lg:h-[500px] rounded-xl overflow-hidden">
                        <img
                            src={displayImages[currentImageIndex]}
                            alt={title}
                            className="w-full h-full object-cover"
                        />

                        {displayImages.length > 1 && (
                            <>
                                <button
                                    onClick={prevImage}
                                    className="absolute left-4 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-black bg-opacity-50 hover:bg-opacity-75 text-white rounded-full flex items-center justify-center transition-all duration-200"
                                >
                                    <ChevronLeft className="w-6 h-6" />
                                </button>
                                <button
                                    onClick={nextImage}
                                    className="absolute right-4 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-black bg-opacity-50 hover:bg-opacity-75 text-white rounded-full flex items-center justify-center transition-all duration-200"
                                >
                                    <ChevronRight className="w-6 h-6" />
                                </button>
                                <div className="absolute bottom-4 right-4 bg-black bg-opacity-50 text-white px-3 py-1 rounded-lg">
                                    {currentImageIndex + 1} / {displayImages.length}
                                </div>
                            </>
                        )}

                        {property.verified && (
                            <div className="absolute top-4 left-4 flex space-x-2">
                                <div className="bg-highland-500 text-white px-3 py-1 rounded-full text-sm font-medium flex items-center space-x-1">
                                    <Shield className="w-3 h-3" />
                                    <span>Verified</span>
                                </div>
                            </div>
                        )}
                    </div>

                    {displayImages.length > 1 && (
                        <div className="flex space-x-2 mt-4 overflow-x-auto pb-2">
                            {displayImages.map((image, index) => (
                                <button
                                    key={index}
                                    onClick={() => setCurrentImageIndex(index)}
                                    className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                                        currentImageIndex === index
                                            ? 'border-highland-500'
                                            : 'border-transparent hover:border-soil-100'
                                    }`}
                                >
                                    <img
                                        src={image}
                                        alt={`View ${index + 1}`}
                                        className="w-full h-full object-cover"
                                    />
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Main Content */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Left Column - Property Details */}
                    <div className="lg:col-span-2 space-y-8">
                        {/* Basic Info */}
                        <div className="bg-white rounded-xl shadow-lg p-6">
                            <div className="flex items-start justify-between mb-4">
                                <div>
                                    <h1 className="font-display text-3xl font-bold text-soil-900 mb-2">{title}</h1>
                                    <div className="flex items-center text-soil-600 mb-2">
                                        <MapPin className="w-4 h-4 mr-1" />
                                        <span>{locationLabel}</span>
                                    </div>
                                    {property.createdAt && (
                                        <div className="flex items-center text-sm text-soil-600">
                                            <Clock className="w-4 h-4 mr-1" />
                                            <span>Listed {new Date(property.createdAt * 1000).toLocaleDateString()}</span>
                                        </div>
                                    )}
                                </div>

                                <div className="text-right">
                                    <div className="font-mono text-3xl font-bold text-highland-500">
                                        {formatPrice(property.pricePerMonth)}
                                    </div>
                                    <div className="text-soil-400">per month</div>
                                </div>
                            </div>

                            {/* Property Features */}
                            <div className="grid grid-cols-3 gap-4 p-4 bg-soil-50 rounded-lg">
                                <div className="text-center">
                                    <Bed className="w-6 h-6 text-soil-600 mx-auto mb-1" />
                                    <div className="font-semibold">{property.bedrooms}</div>
                                    <div className="text-sm text-soil-600">Bedrooms</div>
                                </div>
                                <div className="text-center">
                                    <Bath className="w-6 h-6 text-soil-600 mx-auto mb-1" />
                                    <div className="font-semibold">{property.bathrooms}</div>
                                    <div className="text-sm text-soil-600">Bathrooms</div>
                                </div>
                                <div className="text-center">
                                    <Square className="w-6 h-6 text-soil-600 mx-auto mb-1" />
                                    <div className="font-semibold">{property.squareFeet || 'N/A'}</div>
                                    <div className="text-sm text-soil-600">sqft</div>
                                </div>
                            </div>
                        </div>

                        {/* Description */}
                        {property.propertyDescription && (
                            <div className="bg-white rounded-xl shadow-lg p-6">
                                <h2 className="font-display text-2xl font-bold text-soil-900 mb-4">Description</h2>
                                <p className="text-soil-600 whitespace-pre-line">{property.propertyDescription}</p>
                            </div>
                        )}

                        {/* Property Details */}
                        <div className="bg-white rounded-xl shadow-lg p-6">
                            <h2 className="font-display text-2xl font-bold text-soil-900 mb-4">Property Details</h2>
                            <div className="space-y-3">
                                <div className="flex justify-between">
                                    <span className="text-soil-600">Property Type:</span>
                                    <span className="font-medium capitalize">{propertyTypeName}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-soil-600">Availability:</span>
                                    <span className="font-medium text-highland-600 capitalize">{property.status}</span>
                                </div>
                                {property.furnishingStatus && (
                                    <div className="flex justify-between">
                                        <span className="text-soil-600">Furnishing:</span>
                                        <span className="font-medium capitalize">{property.furnishingStatus}</span>
                                    </div>
                                )}
                                {property.leaseType && (
                                    <div className="flex justify-between">
                                        <span className="text-soil-600">Lease Type:</span>
                                        <span className="font-medium capitalize">{property.leaseType}</span>
                                    </div>
                                )}
                                {property.minimumLeaseTerm && (
                                    <div className="flex justify-between">
                                        <span className="text-soil-600">Minimum Lease Term:</span>
                                        <span className="font-medium">{property.minimumLeaseTerm} month{property.minimumLeaseTerm === 1 ? '' : 's'}</span>
                                    </div>
                                )}
                                {property.securityDeposit != null && (
                                    <div className="flex justify-between">
                                        <span className="text-soil-600">Security Deposit:</span>
                                        <span className="font-medium">{formatPrice(property.securityDeposit)}</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Amenities */}
                        {amenityDetails.length > 0 && (
                            <div className="bg-white rounded-xl shadow-lg p-6">
                                <h2 className="font-display text-2xl font-bold text-soil-900 mb-4">Amenities</h2>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {amenityDetails.map((amenity) => {
                                        const Icon = AMENITY_ICONS[amenity.icon] || Home;
                                        return (
                                            <div key={amenity.id} className="flex items-center space-x-3 p-3 bg-soil-50 rounded-lg">
                                                <Icon className="w-5 h-5 text-highland-500" />
                                                <span className="font-medium text-soil-600">{amenity.name}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Tours & Media */}
                        {(media.videoTourUrl || media.virtualTourUrl || media.otherMediaLinks.length > 0) && (
                            <div className="bg-white rounded-xl shadow-lg p-6">
                                <h2 className="font-display text-2xl font-bold text-soil-900 mb-4">Tours & Media</h2>
                                <div className="space-y-3">
                                    {media.videoTourUrl && (
                                        <a
                                            href={media.videoTourUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center space-x-3 p-3 bg-soil-50 rounded-lg hover:bg-soil-100 transition-colors"
                                        >
                                            <PlayCircle className="w-5 h-5 text-highland-500 flex-shrink-0" />
                                            <span className="font-medium text-soil-600">Watch Video Tour</span>
                                        </a>
                                    )}
                                    {media.virtualTourUrl && (
                                        <a
                                            href={media.virtualTourUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center space-x-3 p-3 bg-soil-50 rounded-lg hover:bg-soil-100 transition-colors"
                                        >
                                            <Box className="w-5 h-5 text-highland-500 flex-shrink-0" />
                                            <span className="font-medium text-soil-600">Explore Virtual Tour (360°)</span>
                                        </a>
                                    )}
                                    {media.otherMediaLinks.map((link, index) => (
                                        <a
                                            key={index}
                                            href={link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center space-x-3 p-3 bg-soil-50 rounded-lg hover:bg-soil-100 transition-colors"
                                        >
                                            <Link2 className="w-5 h-5 text-highland-500 flex-shrink-0" />
                                            <span className="font-medium text-soil-600 truncate">{link}</span>
                                        </a>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Right Column - Contact & Actions */}
                    <div className="space-y-6">
                        {/* Contact Card */}
                        <div className="bg-white rounded-xl shadow-lg p-6 sticky top-24">
                            <div className="text-center mb-6">
                                <div className="w-16 h-16 rounded-full bg-highland-500 flex items-center justify-center mx-auto mb-3">
                                    <Building className="w-8 h-8 text-white" />
                                </div>
                                <h3 className="text-lg font-semibold text-soil-900">{landlordContact.name}</h3>
                                <p className="text-soil-600 text-sm">{landlordContact.subtitle}</p>
                            </div>

                            <div className="space-y-3">
                                <button
                                    onClick={() => setShowApplicationForm(true)}
                                    className="w-full bg-highland-500 hover:bg-highland-600 text-white py-3 px-4 rounded-lg font-medium transition-colors duration-200"
                                >
                                    Apply Now
                                </button>

                                {allowViewingRequests && (
                                    <button
                                        onClick={() => setShowScheduleForm(true)}
                                        className="w-full bg-dawn-500 hover:bg-dawn-600 text-soil-900 py-3 px-4 rounded-lg font-medium transition-colors duration-200 flex items-center justify-center space-x-2"
                                    >
                                        <Calendar className="w-4 h-4" />
                                        <span>Schedule Viewing</span>
                                    </button>
                                )}

                                {(landlordContact.phone || landlordContact.email || landlordContact.whatsapp) && (
                                    <div className="grid grid-cols-3 gap-2">
                                        {landlordContact.phone && (
                                            <a
                                                href={`tel:${landlordContact.phone}`}
                                                className="flex items-center justify-center py-3 px-3 border border-soil-100 hover:border-highland-500 text-soil-600 hover:text-highland-500 rounded-lg transition-colors duration-200"
                                                aria-label="Call"
                                            >
                                                <Phone className="w-5 h-5" />
                                            </a>
                                        )}
                                        {landlordContact.email && (
                                            <a
                                                href={`mailto:${landlordContact.email}`}
                                                className="flex items-center justify-center py-3 px-3 border border-soil-100 hover:border-highland-500 text-soil-600 hover:text-highland-500 rounded-lg transition-colors duration-200"
                                                aria-label="Email"
                                            >
                                                <Mail className="w-5 h-5" />
                                            </a>
                                        )}
                                        {landlordContact.whatsapp && (
                                            <a
                                                href={`https://wa.me/${landlordContact.whatsapp}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center justify-center py-3 px-3 border border-soil-100 hover:border-highland-500 text-soil-600 hover:text-highland-500 rounded-lg transition-colors duration-200"
                                                aria-label="WhatsApp"
                                            >
                                                <MessageCircle className="w-5 h-5" />
                                            </a>
                                        )}
                                    </div>
                                )}

                                <button
                                    onClick={handleOpenContactForm}
                                    className="w-full border border-soil-100 hover:border-highland-500 text-soil-600 hover:text-highland-500 py-3 px-4 rounded-lg font-medium transition-colors duration-200 flex items-center justify-center space-x-2"
                                >
                                    <Send className="w-4 h-4" />
                                    <span>Send Message</span>
                                </button>
                            </div>
                        </div>

                        {/* Monthly Rent */}
                        <div className="bg-white rounded-xl shadow-lg p-6">
                            <h3 className="text-lg font-semibold text-soil-900 mb-4">Monthly Rent</h3>
                            <div className="font-mono text-2xl font-bold text-highland-500">
                                {formatPrice(property.pricePerMonth)}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Image Modal */}
            {showImageModal && (
                <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4">
                    <div className="relative max-w-4xl max-h-full">
                        <button
                            onClick={() => setShowImageModal(false)}
                            className="absolute top-4 right-4 text-white hover:text-karibu-300 z-10"
                        >
                            <X className="w-8 h-8" />
                        </button>

                        <img
                            src={displayImages[currentImageIndex]}
                            alt={title}
                            className="max-w-full max-h-full object-contain"
                        />

                        {displayImages.length > 1 && (
                            <>
                                <button
                                    onClick={prevImage}
                                    className="absolute left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-black bg-opacity-50 hover:bg-opacity-75 text-white rounded-full flex items-center justify-center"
                                >
                                    <ChevronLeft className="w-8 h-8" />
                                </button>
                                <button
                                    onClick={nextImage}
                                    className="absolute right-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-black bg-opacity-50 hover:bg-opacity-75 text-white rounded-full flex items-center justify-center"
                                >
                                    <ChevronRight className="w-8 h-8" />
                                </button>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Contact Form Modal */}
            {showContactForm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl max-w-md w-full max-h-full overflow-y-auto">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-soil-900">Send Message</h3>
                                <button
                                    onClick={() => setShowContactForm(false)}
                                    className="text-soil-400 hover:text-soil-600"
                                >
                                    <X className="w-6 h-6" />
                                </button>
                            </div>

                            <p className="text-sm text-soil-600 mb-4">
                                Your message will be sent directly to {landlordContact.name}, along with your account details.
                            </p>

                            {contactError && (
                                <div className="mb-4 p-3 bg-murram-100/40 border border-murram-100 rounded-lg flex items-center space-x-2">
                                    <AlertCircle className="w-4 h-4 text-murram-500 flex-shrink-0" />
                                    <p className="text-sm text-murram-600">{contactError}</p>
                                </div>
                            )}

                            <form onSubmit={handleContactSubmit} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-soil-600 mb-1">
                                        Message
                                    </label>
                                    <textarea
                                        rows={4}
                                        required
                                        value={contactMessage}
                                        onChange={(e) => setContactMessage(e.target.value)}
                                        className="w-full px-3 py-2 border border-soil-100 rounded-lg focus:ring-2 focus:ring-highland-500 focus:border-transparent"
                                        placeholder="I'm interested in this property..."
                                    />
                                </div>

                                <div className="flex space-x-3">
                                    <button
                                        type="button"
                                        onClick={() => setShowContactForm(false)}
                                        className="flex-1 px-4 py-2 border border-soil-100 text-soil-600 rounded-lg hover:bg-soil-50 transition-colors duration-200"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={contactSubmitting}
                                        className="flex-1 px-4 py-2 bg-highland-500 hover:bg-highland-600 text-white rounded-lg transition-colors duration-200 disabled:opacity-50 flex items-center justify-center space-x-2"
                                    >
                                        {contactSubmitting ? <Loader className="w-4 h-4 animate-spin" /> : <span>Send Message</span>}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Schedule Viewing Modal */}
            {showScheduleForm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl max-w-md w-full max-h-full overflow-y-auto">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-soil-900">Schedule Viewing</h3>
                                <button
                                    onClick={() => setShowScheduleForm(false)}
                                    className="text-soil-400 hover:text-soil-600"
                                >
                                    <X className="w-6 h-6" />
                                </button>
                            </div>

                            {scheduleError && (
                                <div className="mb-4 p-3 bg-murram-100/40 border border-murram-100 rounded-lg flex items-center space-x-2">
                                    <AlertCircle className="w-4 h-4 text-murram-500 flex-shrink-0" />
                                    <p className="text-sm text-murram-600">{scheduleError}</p>
                                </div>
                            )}

                            <form onSubmit={handleScheduleSubmit} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-soil-600 mb-1">
                                        Preferred Date
                                    </label>
                                    <input
                                        type="date"
                                        required
                                        value={scheduleForm.date}
                                        onChange={(e) => setScheduleForm(prev => ({ ...prev, date: e.target.value }))}
                                        className="w-full px-3 py-2 border border-soil-100 rounded-lg focus:ring-2 focus:ring-highland-500 focus:border-transparent"
                                        min={new Date().toISOString().split('T')[0]}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-soil-600 mb-1">
                                        Preferred Time
                                    </label>
                                    <select
                                        required
                                        value={scheduleForm.time}
                                        onChange={(e) => setScheduleForm(prev => ({ ...prev, time: e.target.value }))}
                                        className="w-full px-3 py-2 border border-soil-100 rounded-lg focus:ring-2 focus:ring-highland-500 focus:border-transparent"
                                    >
                                        <option value="">Select a time</option>
                                        <option value="09:00">9:00 AM</option>
                                        <option value="10:00">10:00 AM</option>
                                        <option value="11:00">11:00 AM</option>
                                        <option value="14:00">2:00 PM</option>
                                        <option value="15:00">3:00 PM</option>
                                        <option value="16:00">4:00 PM</option>
                                        <option value="17:00">5:00 PM</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-soil-600 mb-1">
                                        Additional Notes (Optional)
                                    </label>
                                    <textarea
                                        rows={3}
                                        value={scheduleForm.message}
                                        onChange={(e) => setScheduleForm(prev => ({ ...prev, message: e.target.value }))}
                                        className="w-full px-3 py-2 border border-soil-100 rounded-lg focus:ring-2 focus:ring-highland-500 focus:border-transparent"
                                        placeholder="Any specific requirements or questions..."
                                    />
                                </div>

                                <div className="flex space-x-3">
                                    <button
                                        type="button"
                                        onClick={() => setShowScheduleForm(false)}
                                        className="flex-1 px-4 py-2 border border-soil-100 text-soil-600 rounded-lg hover:bg-soil-50 transition-colors duration-200"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={scheduleSubmitting}
                                        className="flex-1 px-4 py-2 bg-dawn-500 hover:bg-dawn-600 text-soil-900 rounded-lg transition-colors duration-200 disabled:opacity-50 flex items-center justify-center space-x-2"
                                    >
                                        {scheduleSubmitting ? (
                                            <Loader className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <span>Schedule Viewing</span>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Application Modal */}
            {showApplicationForm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl max-w-md w-full max-h-full overflow-y-auto">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-soil-900">Apply for Property</h3>
                                <button
                                    onClick={() => setShowApplicationForm(false)}
                                    className="text-soil-400 hover:text-soil-600"
                                >
                                    <X className="w-6 h-6" />
                                </button>
                            </div>

                            <div className="text-center py-8">
                                <Home className="w-16 h-16 text-highland-500 mx-auto mb-4" />
                                <h4 className="text-xl font-semibold text-soil-900 mb-2">Start Your Application</h4>
                                <p className="text-soil-600 mb-6">
                                    You'll be redirected to the application form where you can provide your details
                                    and submit required documents.
                                </p>

                                <div className="space-y-3">
                                    <button
                                        onClick={() => {
                                            setShowApplicationForm(false);
                                            navigate(`/apply/${id}`);
                                        }}
                                        className="w-full bg-highland-500 hover:bg-highland-600 text-white py-3 px-4 rounded-lg font-medium transition-colors duration-200"
                                    >
                                        Continue to Application
                                    </button>
                                    <button
                                        onClick={() => setShowApplicationForm(false)}
                                        className="w-full border border-soil-100 text-soil-600 py-3 px-4 rounded-lg hover:bg-soil-50 transition-colors duration-200"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PropertyDetail;
